import { useSeason } from "../../context/SeasonContext";
import { PublicHeader } from "../../components/Header/PublicHeader";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SeasonLink as Link } from "../../components/Seasons/SeasonLink";
import {
  getCasePriority,
  getMyPriorityCases,
  getPublicShowcaseCase,
  getPublicShowcaseCases,
  getPublicShowcaseTracks,
  setCasePriority,
} from "../../api/caseShowcase";
import { CaseModal } from "../../components/CaseModal/CaseModal";
import { CaseArtwork } from "../../components/CaseArtwork/CaseArtwork.jsx";
import { useAuth } from "../../context/AuthContext";
import { caseTitle } from "../../utils/caseTitle";
import { casePriorityAccess } from "../../utils/casePriorityAccess";
import styles from "./caseShowcase.module.css";

const PAGE_SIZE = 12;
const PRIORITY_PREVIEW_LIMIT = 200;
const PRIORITY_FALLBACK_LIMIT = 10;
const VIEW_STORAGE_KEY = "sfedu_case_showcase_view";

const TRACK_DISPLAY_NAMES = {
  Партнеры: "ЮФУ.Про Партнеров",
  Стартапы: "ЮФУ.Про Стартапы",
  Исследования: "ЮФУ.Про Исследования",
  Общество: "ЮФУ.Про Общество",
  Организация: "ЮФУ.Про Организацию",
};

function SearchIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </svg>
  );
}

function ViewIcon({ cards }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden="true"
    >
      {cards ? (
        <>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </>
      ) : (
        <path d="M3 5h3m4 0h11M3 12h3m4 0h11M3 19h3m4 0h11" />
      )}
    </svg>
  );
}

export function CaseShowcasePage() {
  const { canEdit, season, archiveEdit } = useSeason();
  const { isAuthenticated } = useAuth();
  const { canSetPriority, reason: priorityUnavailable } = casePriorityAccess({
    season,
    canEdit,
    archiveEdit,
    isAuthenticated,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [casePriorities, setCasePriorities] = useState({});
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [partner, setPartner] = useState("");
  const [location, setLocation] = useState("");
  const [hasAttachments, setHasAttachments] = useState(false);
  const [sortBy, setSortBy] = useState("sort_order");
  const [sortDir, setSortDir] = useState("asc");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem(VIEW_STORAGE_KEY) === "cards"
        ? "cards"
        : "list";
    } catch {
      return "list";
    }
  });
  const cardView = view === "cards";
  const [retryCount, setRetryCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [activeTrackTab, setActiveTrackTab] = useState("");
  const [tracks, setTracks] = useState([]);
  const [tracksError, setTracksError] = useState("");
  const trackTabs = [
    { label: "Все треки", value: "" },
    ...tracks.map((value) => ({ label: value, value })),
  ];
  const [selectedCase, setSelectedCase] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [priorityPreview, setPriorityPreview] = useState([]);
  const focusCaseId = useMemo(() => {
    const value = searchParams.get("focus");
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < total;

  const normalizePartnerName = (value) => {
    if (!value) return "";
    return value.trim() === "ЮФУ.проШкольников"
      ? "Центр тестирования и подготовки к олимпиадам АПП ЮФУ"
      : value;
  };

  const getTrackDisplayName = (value) => TRACK_DISPLAY_NAMES[value] || value;

  useEffect(() => {
    let cancelled = false;
    setTracks([]);
    setTracksError("");
    getPublicShowcaseTracks()
      .then((values) => {
        if (!cancelled) setTracks(values);
      })
      .catch(() => {
        if (!cancelled) setTracksError("Не удалось загрузить треки.");
      });
    return () => {
      cancelled = true;
    };
  }, [season?.id, retryCount]);

  const loadCasePriorities = useCallback(
    async (caseItems) => {
      if (!isAuthenticated || !caseItems.length) {
        setCasePriorities({});
        return;
      }

      const priorities = await Promise.all(
        caseItems.map(async (item) => {
          try {
            const response = await getCasePriority(item.case_id);
            return [item.case_id, response?.priority ?? null];
          } catch {
            return [item.case_id, null];
          }
        }),
      );

      setCasePriorities(Object.fromEntries(priorities));
    },
    [isAuthenticated],
  );

  const loadPriorityPreview = useCallback(async () => {
    if (!isAuthenticated) {
      setPriorityPreview([]);
      return;
    }

    try {
      let response;
      try {
        response = await getMyPriorityCases({ limit: PRIORITY_PREVIEW_LIMIT });
      } catch {
        response = await getMyPriorityCases({ limit: PRIORITY_FALLBACK_LIMIT });
      }
      setPriorityPreview(response?.items || []);
    } catch {
      setPriorityPreview([]);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timerId = window.setTimeout(async () => {
      setError("");
      try {
        const response = await getPublicShowcaseCases({
          query,
          partner,
          track: activeTrackTab || undefined,
          location,
          has_attachments: hasAttachments ? true : undefined,
          sort_by: sortBy,
          sort_dir: sortDir,
          limit: PAGE_SIZE,
          offset,
        });
        if (!cancelled) {
          setItems(response?.items || []);
          setTotal(response?.total || 0);
        }
      } catch (err) {
        if (!cancelled) {
          setItems([]);
          setTotal(0);
          setError(err.message || "Не удалось загрузить витрину кейсов.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [
    query,
    partner,
    activeTrackTab,
    location,
    hasAttachments,
    sortBy,
    sortDir,
    offset,
    retryCount,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !items.length) {
      setCasePriorities({});
      return;
    }

    loadCasePriorities(items).catch(() => setCasePriorities({}));
  }, [isAuthenticated, items, loadCasePriorities]);

  useEffect(() => {
    if (!isAuthenticated) {
      setPriorityPreview([]);
      return;
    }

    loadPriorityPreview().catch(() => setPriorityPreview([]));
  }, [isAuthenticated, isModalOpen, loadPriorityPreview]);

  useEffect(() => {
    if (!focusCaseId) return;

    const fromList = items.find((item) => item.case_id === focusCaseId);
    if (fromList) {
      setSelectedCase(fromList);
      setIsModalOpen(true);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await getPublicShowcaseCase(focusCaseId);
        if (!cancelled && data?.case_id) {
          setSelectedCase(data);
          setIsModalOpen(true);
        }
      } catch {
        // focus id can be stale
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [focusCaseId, items]);

  const handleTrackTabClick = (trackValue) => {
    setActiveTrackTab(trackValue);
    setOffset(0);
  };

  const handleOpenModal = (caseItem) => {
    setSelectedCase(caseItem);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCase(null);
    if (focusCaseId) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("focus");
      setSearchParams(nextParams, { replace: true });
    }
  };

  const handleCaseChanged = () => {
    loadCasePriorities(items).catch(() => setCasePriorities({}));
    loadPriorityPreview().catch(() => setPriorityPreview([]));
  };

  const handleResetMyCaseSelection = async () => {
    if (!isAuthenticated) {
      setError("Чтобы сбросить приоритеты, сначала войдите в платформу.");
      setNotice("");
      return;
    }
    if (!canSetPriority || resetLoading) return;
    const confirmed = window.confirm(
      "Сбросить все выбранные приоритеты кейсов (П1, П2, П3)?",
    );
    if (!confirmed) return;

    setResetLoading(true);
    setError("");
    setNotice("");
    try {
      let response;
      try {
        response = await getMyPriorityCases({ limit: PRIORITY_PREVIEW_LIMIT });
      } catch {
        response = await getMyPriorityCases({ limit: PRIORITY_FALLBACK_LIMIT });
      }
      const selected = response?.items || [];
      if (!selected.length) {
        setNotice("У вас нет установленных приоритетов.");
        return;
      }

      const results = await Promise.allSettled(
        selected.map((item) => setCasePriority(item.case_id, null)),
      );
      const successCount = results.filter(
        (item) => item.status === "fulfilled",
      ).length;
      const failedCount = results.length - successCount;

      await Promise.all([loadPriorityPreview(), loadCasePriorities(items)]);
      setNotice(
        failedCount
          ? `Сбросили ${successCount} приоритетов, не удалось сбросить ${failedCount}.`
          : `Ваш выбор кейсов успешно сброшен (${successCount}).`,
      );
    } catch (err) {
      setError(err.message || "Не удалось сбросить выбор кейсов.");
    } finally {
      setResetLoading(false);
    }
  };

  const hasFilters = Boolean(
    query || partner || activeTrackTab || location || hasAttachments,
  );

  const handleClearFilters = () => {
    setQuery("");
    setPartner("");
    setActiveTrackTab("");
    setLocation("");
    setHasAttachments(false);
    setSortBy("sort_order");
    setSortDir("asc");
    setOffset(0);
  };

  const changeView = (nextView) => {
    setView(nextView);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, nextView);
    } catch {
      // The view still works when browser storage is unavailable.
    }
  };

  return (
    <div className={styles.pageShell}>
      <div className={styles.page}>
        <PublicHeader />
        <main>
          <header className={styles.hero}>
            <h1>
              Биржа кейсов <span>ЮФУ.Про</span>
            </h1>
            <p>
              Компании формулируют реальные задачи —
              <br />
              студенты выбирают и реализуют проект.
            </p>
          </header>

          <div className={styles.searchBar}>
            <label className={styles.searchField}>
              <SearchIcon />
              <input
                className={styles.searchInput}
                type="search"
                aria-label="Поиск по кейсам"
                placeholder="Название кейса, специализация или что-то другое..."
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setOffset(0);
                }}
              />
            </label>
            <select
              className={styles.sortSelect}
              aria-label="Сортировать кейсы"
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setOffset(0);
              }}
            >
              <option value="sort_order">Сначала рекомендованные</option>
              <option value="source_created_at">По дате заявки</option>
              <option value="created_at">По дате добавления</option>
              <option value="partner_name">По партнёру</option>
              <option value="title">По названию</option>
            </select>
            <button
              type="button"
              className={styles.mobileFiltersButton}
              aria-expanded={showMobileFilters}
              aria-controls="case-filters"
              onClick={() => setShowMobileFilters((open) => !open)}
            >
              {showMobileFilters ? "Скрыть фильтры" : "Показать фильтры"}
              {hasFilters ? (
                <span
                  className={styles.filterDot}
                  aria-label="Фильтры применены"
                />
              ) : null}
            </button>
          </div>

          <div className={styles.mainContent}>
            <section
              className={styles.caseList}
              aria-label="Список кейсов"
              aria-busy={loading}
            >
              <div className={styles.resultsToolbar}>
                <p className={styles.casesCount} role="status">
                  {loading
                    ? "Загружаем кейсы..."
                    : `Показано ${items.length} из ${total} кейсов`}
                </p>
                <div
                  className={styles.viewToggle}
                  role="group"
                  aria-label="Вид кейсов"
                >
                  <button
                    type="button"
                    className={styles.viewButton}
                    aria-pressed={!cardView}
                    onClick={() => changeView("list")}
                  >
                    <ViewIcon /> Список
                  </button>
                  <button
                    type="button"
                    className={styles.viewButton}
                    aria-pressed={cardView}
                    onClick={() => changeView("cards")}
                  >
                    <ViewIcon cards /> Карточки
                  </button>
                </div>
              </div>
              {error ? (
                <div className={styles.error} role="alert">
                  <p>{error}</p>
                  <button
                    type="button"
                    className={styles.textButton}
                    onClick={() => setRetryCount((count) => count + 1)}
                  >
                    Попробовать ещё раз
                  </button>
                </div>
              ) : null}
              {notice ? (
                <p className={styles.notice} role="status">
                  {notice}
                </p>
              ) : null}
              {loading ? (
                <div
                  className={`${styles.loadingCards} ${cardView ? styles.cardsGrid : ""}`}
                  aria-hidden="true"
                >
                  {Array.from({ length: cardView ? 6 : 3 }, (_, key) => (
                    <div key={key} className={styles.skeletonCard} />
                  ))}
                </div>
              ) : !error && !items.length ? (
                <div className={styles.emptyState}>
                  <h2>
                    {hasFilters ? "Кейсы не найдены" : "Кейсы скоро появятся"}
                  </h2>
                  <p>
                    {hasFilters
                      ? "Попробуйте изменить запрос или очистить фильтры."
                      : "Пока нет опубликованных кейсов. Загляните сюда позже."}
                  </p>
                  {hasFilters ? (
                    <button
                      type="button"
                      className={styles.textButton}
                      onClick={handleClearFilters}
                    >
                      Очистить фильтры
                    </button>
                  ) : null}
                </div>
              ) : (
                <div
                  className={`${styles.cards} ${cardView ? styles.cardsGrid : ""}`}
                >
                  {items.map((item) => {
                    const priority = Object.prototype.hasOwnProperty.call(
                      casePriorities,
                      item.case_id,
                    )
                      ? casePriorities[item.case_id]
                      : item.user_priority;
                    return (
                      <article key={item.case_id} className={styles.card}>
                        <button
                          type="button"
                          className={styles.cardButton}
                          aria-label={`Открыть кейс ${caseTitle(item)}`}
                          onClick={() => handleOpenModal(item)}
                        >
                          <span className={styles.cardHeader}>
                            <CaseArtwork
                              caseItem={item}
                              className={styles.partnerAvatar}
                            />
                            <span className={styles.cardInfo}>
                              <span className={styles.partner}>
                                {normalizePartnerName(item.partner_name) ||
                                  "ЮФУ.Про"}
                              </span>
                              {!cardView && (
                                <span className={styles.caseTitle}>
                                  {caseTitle(item)}
                                </span>
                              )}
                            </span>
                          </span>
                          {cardView && (
                            <>
                              <span
                                className={styles.caseTitle}
                                title={caseTitle(item)}
                              >
                                {caseTitle(item)}
                              </span>
                              {item.problem && (
                                <span className={styles.caseDescription}>
                                  {item.problem}
                                </span>
                              )}
                            </>
                          )}
                          <span className={styles.cardTags}>
                            {item.track ? (
                              <span className={styles.trackBadge}>
                                {getTrackDisplayName(item.track)}
                              </span>
                            ) : null}
                            {item.location ? (
                              <span className={styles.outlineBadge}>
                                {item.location}
                              </span>
                            ) : null}
                            {priority ? (
                              <span className={styles.priorityBadge}>
                                Мой приоритет П{priority}
                              </span>
                            ) : null}
                          </span>
                          {cardView && (
                            <span className={styles.cardFooter}>
                              Открыть кейс
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.7"
                                aria-hidden="true"
                              >
                                <path d="M5 19 19 5M5 5h14v14" />
                              </svg>
                            </span>
                          )}
                        </button>
                      </article>
                    );
                  })}
                </div>
              )}

              {!loading && !error && totalPages > 1 ? (
                <nav className={styles.pagination} aria-label="Страницы кейсов">
                  <button
                    type="button"
                    className={styles.ghostBtn}
                    onClick={() =>
                      setOffset((value) => Math.max(0, value - PAGE_SIZE))
                    }
                    disabled={!hasPrev}
                  >
                    Назад
                  </button>
                  <span>
                    Страница {page} из {totalPages}
                  </span>
                  <button
                    type="button"
                    className={styles.ghostBtn}
                    onClick={() => setOffset((value) => value + PAGE_SIZE)}
                    disabled={!hasNext}
                  >
                    Далее
                  </button>
                </nav>
              ) : null}
            </section>

            <aside
              id="case-filters"
              className={`${styles.sidebar} ${showMobileFilters ? styles.sidebarOpen : ""}`}
              aria-label="Фильтры кейсов"
            >
              <div className={styles.filters}>
                <fieldset className={styles.filterSection}>
                  <legend className={styles.filterTitle}>Тип трека</legend>
                  {tracksError && (
                    <p role="alert">
                      {tracksError}{" "}
                      <button
                        type="button"
                        onClick={() => setRetryCount((value) => value + 1)}
                      >
                        Повторить
                      </button>
                    </p>
                  )}
                  <div className={styles.filterOptions}>
                    {trackTabs.map((tab) => (
                      <label key={tab.value} className={styles.filterOption}>
                        <input
                          type="radio"
                          name="case-track"
                          value={tab.value}
                          checked={activeTrackTab === tab.value}
                          onChange={() => handleTrackTabClick(tab.value)}
                        />
                        <span>{tab.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div className={styles.filterSection}>
                  <label className={styles.filterTitle} htmlFor="case-partner">
                    Партнёр
                  </label>
                  <div className={styles.partnerSearch}>
                    <SearchIcon />
                    <input
                      id="case-partner"
                      className={styles.input}
                      type="search"
                      placeholder="Найти партнёра"
                      value={partner}
                      onChange={(e) => {
                        setPartner(e.target.value);
                        setOffset(0);
                      }}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className={styles.advancedToggle}
                  aria-expanded={showAdvancedFilters}
                  aria-controls="advanced-case-filters"
                  onClick={() => setShowAdvancedFilters((open) => !open)}
                >
                  Дополнительные фильтры{" "}
                  <span aria-hidden="true">
                    {showAdvancedFilters ? "−" : "+"}
                  </span>
                </button>
                {showAdvancedFilters ? (
                  <div
                    id="advanced-case-filters"
                    className={styles.advancedFilters}
                  >
                    <label className={styles.fieldLabel}>
                      Локация
                      <input
                        className={styles.input}
                        type="text"
                        placeholder="Город или подразделение"
                        value={location}
                        onChange={(e) => {
                          setLocation(e.target.value);
                          setOffset(0);
                        }}
                      />
                    </label>
                    <label className={styles.fieldLabel}>
                      Порядок сортировки
                      <select
                        className={styles.input}
                        value={sortDir}
                        onChange={(e) => {
                          setSortDir(e.target.value);
                          setOffset(0);
                        }}
                      >
                        <option value="asc">По возрастанию</option>
                        <option value="desc">По убыванию</option>
                      </select>
                    </label>
                    <label className={styles.filterOption}>
                      <input
                        type="checkbox"
                        checked={hasAttachments}
                        onChange={(e) => {
                          setHasAttachments(e.target.checked);
                          setOffset(0);
                        }}
                      />
                      <span>Только с материалами</span>
                    </label>
                  </div>
                ) : null}
                <button
                  type="button"
                  className={styles.clearFilters}
                  onClick={handleClearFilters}
                >
                  Очистить фильтры
                </button>
              </div>

              <div className={styles.selectionPanel}>
                <h2>Мой выбор кейсов</h2>
                <p>
                  {priorityUnavailable ||
                    "Откройте кейс и укажите приоритет. Можно выбрать несколько проектов."}
                </p>
                {isAuthenticated ? (
                  <>
                    <details className={styles.priorityDetails}>
                      <summary>
                        Расставлено приоритетов: {priorityPreview.length}
                      </summary>
                      {priorityPreview.length ? (
                        <ul className={styles.priorityList}>
                          {priorityPreview.map((item) => (
                            <li key={item.case_id}>
                              <button
                                type="button"
                                className={styles.priorityLink}
                                onClick={() => handleOpenModal(item)}
                              >
                                <span>П{item.user_priority}</span>{" "}
                                {caseTitle(item)}
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p>Приоритеты пока не расставлены.</p>
                      )}
                    </details>
                    <button
                      type="button"
                      className={styles.clearFilters}
                      onClick={handleResetMyCaseSelection}
                      disabled={resetLoading || !canSetPriority}
                    >
                      {resetLoading
                        ? "Сбрасываем..."
                        : "Сбросить мой выбор кейсов"}
                    </button>
                  </>
                ) : (
                  <Link to="/login" className={styles.primaryBtn}>
                    Войти в платформу
                  </Link>
                )}
              </div>
            </aside>
          </div>
        </main>

        <CaseModal
          caseItem={selectedCase}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onCaseChanged={handleCaseChanged}
        />
      </div>
    </div>
  );
}
